import React, { Suspense, useEffect, useRef } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnyChatForm, MobilePreviewProps } from '@anyx/feature-crm/shared';
import { AlertIcon } from '@anyx/shared-icons';

import { LineMessageBubble } from '../../line-message-bubble';
import { useLineMessageStore } from '../store';
import { LineMessageFormFieldsProps } from '../types';

const ButtonTypePreview = React.lazy(() => import('./ButtonTypePreview'));
const ImageVideoPreview = React.lazy(() => import('./ImageVideoPreview'));
const ImageTypePreview = React.lazy(() => import('./ImageTypePreview'));

type PreviewProps = MobilePreviewProps;

export const Preview = (props: PreviewProps) => {
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('crm');
  const {
    control,
    formState: { errors },
  } = useFormContext<LineMessageFormFieldsProps>();

  const messages = useWatch({
    control,
    name: 'messages',
  });

  const view = useLineMessageStore((state) => state.view);

  /**
   * auto scroll into preview position when a block is navigated
   */
  useEffect(() => {
    if (view.blockIndex !== undefined && previewContainerRef.current) {
      const children = previewContainerRef.current.children;
      const currentChild = children[view.blockIndex] as HTMLElement;

      if (currentChild) {
        previewContainerRef.current.scrollTo({
          top: currentChild?.offsetTop - 40,
          behavior: 'smooth',
        });
      }
    }
  }, [view.blockIndex]);

  return (
    <AnyChatForm.MobilePreview {...props} ref={previewContainerRef}>
      {messages.map((props, blockIndex) => {
        const id = `${props.type}-${blockIndex}`;

        const commonProps = { id, blockIndex };

        return (
          <Suspense fallback={<div />} key={id}>
            <div className="space-y-1">
              {!!errors?.messages?.[blockIndex] && (
                <div className="flex items-center gap-x-1">
                  <AlertIcon className="text-gray-9 h-4 w-4 shrink-0 opacity-30" />
                  <span className="text-failure text-3xs italic">
                    {t('crm.lineMessageForm.validations.missingRequiredInfo')}
                  </span>
                </div>
              )}
              {/* Plain Text */}
              {props.type === 'PLAIN_TEXT' && <AnyChatForm.TextPreview text={props.text} />}
              {/* Rich Message */}
              {props.type === 'RICH' && <LineMessageBubble.RichMessage {...props} />}
              {/* Image / Video */}
              {props.type === 'IMAGE_VIDEO' && <ImageVideoPreview />}
              {/* Button Type */}
              {props.type === 'BUTTON' && <ButtonTypePreview {...props} {...commonProps} />}
              {/* Image Type */}
              {props.type === 'IMAGES' && <ImageTypePreview {...props} {...commonProps} />}
            </div>
          </Suspense>
        );
      })}
    </AnyChatForm.MobilePreview>
  );
};
